import type {
  ConnectionEvent,
  ErrorEvent,
  ScannerStatus,
  TagEvent,
} from './types'

const DEFAULT_WS_URL = 'ws://127.0.0.1:18765'
const BRIDGE_READY_EVENT = 'leap-bridge-ready'

function bridgeWsUrl(): string {
  const raw = import.meta.env.VITE_LEAP_RFID_BRIDGE_WS
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  return DEFAULT_WS_URL
}

let ws: WebSocket | null = null
let ready = false
let connectPromise: Promise<boolean> | null = null
let reqId = 0
const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>()
const tagHandlers = new Set<(ev: TagEvent) => void>()
const connectionHandlers = new Set<(ev: ConnectionEvent) => void>()
const errorHandlers = new Set<(ev: ErrorEvent) => void>()

function notifyReady() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(BRIDGE_READY_EVENT))
}

function parseJson<T>(raw: string, fallback: T): T {
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function handleMessage(raw: string) {
  const msg = parseJson<Record<string, unknown>>(raw, {})
  if (typeof msg.event === 'string') {
    const data = (msg.data ?? {}) as Record<string, unknown>
    switch (msg.event) {
      case 'onTag':
        tagHandlers.forEach((h) => h(data as TagEvent))
        break
      case 'onConnection':
        connectionHandlers.forEach((h) => h(data as ConnectionEvent))
        break
      case 'onError':
        errorHandlers.forEach((h) => h(data as ErrorEvent))
        break
      case 'bridgeReady':
        if (!ready) {
          ready = true
          notifyReady()
        }
        break
      default:
        break
    }
    return
  }

  const id = msg.id
  if (id == null) return
  const entry = pending.get(Number(id))
  if (!entry) return
  pending.delete(Number(id))
  if (msg.error) {
    entry.reject(new Error(String(msg.error)))
    return
  }
  entry.resolve(msg.result)
}

function rpc<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    return Promise.reject(new Error('RFID bridge WebSocket not connected'))
  }
  const id = ++reqId
  return new Promise<T>((resolve, reject) => {
    pending.set(id, {
      resolve: (v) => resolve(v as T),
      reject,
    })
    try {
      ws?.send(JSON.stringify({ id, method, params }))
    } catch (e) {
      pending.delete(id)
      reject(e instanceof Error ? e : new Error('WebSocket send failed'))
    }
  })
}

function scheduleReconnect() {
  if (typeof window === 'undefined') return
  if (window.LeapRfidNative) return
  window.setTimeout(() => {
    void initRemoteBridge()
  }, 2000)
}

export function isRemoteBridgeReady(): boolean {
  return ready
}

export async function initRemoteBridge(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  if (window.LeapRfidNative) return true
  if (ready && ws?.readyState === WebSocket.OPEN) return true
  if (connectPromise) return connectPromise

  connectPromise = new Promise<boolean>((resolve) => {
    try {
      if (ws) {
        ws.onclose = null
        ws.onerror = null
        ws.onmessage = null
        ws.close()
      }
      const socket = new WebSocket(bridgeWsUrl())
      ws = socket

      socket.onopen = () => {
        ready = true
        notifyReady()
        resolve(true)
        connectPromise = null
      }

      socket.onmessage = (ev) => {
        if (typeof ev.data === 'string') handleMessage(ev.data)
      }

      socket.onerror = () => {
        ready = false
        resolve(false)
        connectPromise = null
        scheduleReconnect()
      }

      socket.onclose = () => {
        ready = false
        ws = null
        for (const [, p] of pending) {
          p.reject(new Error('RFID bridge disconnected'))
        }
        pending.clear()
        scheduleReconnect()
      }
    } catch {
      ready = false
      resolve(false)
      connectPromise = null
      scheduleReconnect()
    }
  })

  return connectPromise
}

export async function remoteGetStatus(): Promise<ScannerStatus | null> {
  if (!ready) return null
  try {
    return await rpc<ScannerStatus>('getStatus')
  } catch {
    return null
  }
}

export async function remoteListRfd8500(): Promise<
  Array<{ name: string; address?: string; type?: string }>
> {
  if (!ready) return []
  try {
    const raw = await rpc<unknown>('listRfd8500')
    return Array.isArray(raw) ? (raw as Array<{ name: string; address?: string; type?: string }>) : []
  } catch {
    return []
  }
}

export async function remoteConnect(
  type: string,
  name?: string,
  address?: string
): Promise<void> {
  await rpc('connect', { type, name: name ?? null, address: address ?? null })
}

export async function remoteConnectRfd40Auto(): Promise<void> {
  await rpc('connectRfd40Auto')
}

export async function remoteDisconnect(): Promise<void> {
  await rpc('disconnect')
}

export function subscribeRemoteTag(handler: (ev: TagEvent) => void): () => void {
  tagHandlers.add(handler)
  return () => tagHandlers.delete(handler)
}

export function subscribeRemoteConnection(
  handler: (ev: ConnectionEvent) => void
): () => void {
  connectionHandlers.add(handler)
  return () => connectionHandlers.delete(handler)
}

export function subscribeRemoteError(handler: (ev: ErrorEvent) => void): () => void {
  errorHandlers.add(handler)
  return () => errorHandlers.delete(handler)
}

export { BRIDGE_READY_EVENT }
